import React, { useState, useEffect } from 'react'
import { useUser } from 'util/UserContext'
import { useNavigate } from 'react-router-dom'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'ui/InputSelect'
import { updatePrivateAccountStatus, updateMessagePrivacy, updateDefaultAudience } from 'util/userUtils'
import { PrivacyLevel } from 'util/userUtils'
import { Globe, Users, UserCheck, HeartHandshake, Lock } from 'lucide-react'

const UserSettingsPrivacy: React.FC = () => {
    const { currentUser, awaitingUser } = useUser()
    const navigate = useNavigate()
    const [isPrivate, setIsPrivate] = useState<boolean>(false)
    const [defaultAudience, setDefaultAudience] = useState<PrivacyLevel>(currentUser?.properties_public?.is_private ? 'follow' : 'public')
    const [msgEveryone, setMsgEveryone] = useState<boolean>(true)
    const [msgTeams, setMsgTeams] = useState<boolean>(false)
    const [msgFollowers, setMsgFollowers] = useState<boolean>(false)

    // Initialize settings from currentUser
    useEffect(() => {
        if (!awaitingUser && !currentUser) {
            navigate('/login', { replace: true })
            return
        }

        // Initialize private account setting
        const isUserPrivate = currentUser?.properties_public?.is_private === undefined ? false : currentUser?.properties_public?.is_private
        setIsPrivate(isUserPrivate)
        
        // Initialize default audience
        const savedAudience = currentUser?.properties?.privacy_content_default_audience.value ? currentUser?.properties?.privacy_content_default_audience.value : currentUser?.properties_public?.is_private ? 'follow' : 'public'
        setDefaultAudience(savedAudience as PrivacyLevel)

        // Initialize message settings - simple array check
        const messageSettings = currentUser?.properties?.privacy_message_receive?.value ?? ['public']
        setMsgEveryone(messageSettings.includes('public'))
        setMsgTeams(messageSettings.includes('teams'))
        setMsgFollowers(messageSettings.includes('follow'))
    }, [currentUser, awaitingUser, navigate])

    const handlePrivateAccountChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPrivateValue = e.target.checked
        try {
            const response = await updatePrivateAccountStatus(newPrivateValue)
            const updatedPrivateValue = response.data.properties_public?.is_private ?? false
            setIsPrivate(updatedPrivateValue)
            if (currentUser) {
                currentUser.properties_public = {
                    ...currentUser.properties_public,
                    is_private: updatedPrivateValue
                }
            }
        } catch (error) {
            console.error('Error updating privacy settings:', error)
        }
    }

    const handleMessagePrivacyChange = async (type: 'public' | 'teams' | 'follow', checked: boolean) => {
        try {
            // Create new array based on current states, excluding the one being changed
            let newValues: ('public' | 'teams' | 'follow')[] = []
                
            // Set the new state for the changed checkbox
            if (type === 'public') {
                setMsgEveryone(checked)
            } else if (type === 'teams') {
                setMsgTeams(checked)
            } else if (type === 'follow') {
                setMsgFollowers(checked)
            }
    
            // Build array based on new states
            if (type === 'public' ? checked : msgEveryone) newValues.push('public')
            if (type === 'teams' ? checked : msgTeams) newValues.push('teams')
            if (type === 'follow' ? checked : msgFollowers) newValues.push('follow')
    
            // No need for a default value - empty array is allowed
            await updateMessagePrivacy(newValues)
            if (currentUser) {
                currentUser.properties.privacy_message_receive.value = newValues
            }
        } catch (error) {
            console.error('Error updating message privacy:', error)
        }
    }

    const handleDefaultAudienceChange = async (value: PrivacyLevel) => {
        try {
            await updateDefaultAudience(value)
            setDefaultAudience(value)
            if (currentUser) {
                currentUser.properties.privacy_content_default_audience.value = value
            }
        } catch (error) {
            console.error('Error updating default audience:', error)
        }
    }

    // Define privacy options with icons for visual representation
    const privacyOptions = [
        { value: 'me', label: 'Just me', icon: Lock },
        { value: 'besteas', label: 'Besteas', icon: HeartHandshake },
        { value: 'follow', label: 'Followers', icon: UserCheck },
        { value: 'teams', label: 'Parteas', icon: Users },
        { value: 'public', label: 'Everyone', icon: Globe }
    ]

    if (!currentUser || awaitingUser) {
        return (
            <LoadingSkeleton count={4} height="h-20" className=""/>
        )
    }

    return (
        <div className="p-2">
            <div className="space-y-3">
                {/* Interactions Section */}
                <div className="space-y-2">
                    <label className="form-label text-base font-medium">Interactions</label>
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Private account</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">
                                When enabled, approval is required to follow you.
                            </p>
                        </div>
                        <input
                            type="checkbox"
                            checked={isPrivate}
                            onChange={handlePrivateAccountChange}
                            className="toggle-switch-input col-span-1 flex-shrink-0"
                        />
                    </div>
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover opacity-50 rounded-md cursor-not-allowed">
                        <div className="flex flex-col items-start flex-grow">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Messages (Coming Soon)</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub mb-4">
                                Choose who can send message you.
                            </p>
                            <div className="w-full space-y-2">
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><Globe className="size-4" /> Everyone</span>
                                    <input
                                        type="checkbox"
                                        checked={msgEveryone}
                                        disabled={true}
                                        onChange={(e) => handleMessagePrivacyChange('public', e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0 cursor-not-allowed"
                                    />
                                </div>
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><Users className="size-4" /> Parteas</span>
                                    <input
                                        type="checkbox"
                                        checked={msgTeams}
                                        disabled={true}
                                        onChange={(e) => handleMessagePrivacyChange('teams', e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0 cursor-not-allowed"
                                    />
                                </div>
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><UserCheck className="size-4" /> Followers</span>
                                    <input
                                        type="checkbox"
                                        checked={msgFollowers}
                                        disabled={true}
                                        onChange={(e) => handleMessagePrivacyChange('follow', e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0 cursor-not-allowed"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="space-y-2">
                    <label className="form-label text-base font-medium">Content</label>
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex flex-col items-start flex-grow">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Default audience</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub mb-4">
                                Choose the default audience when publishing content; you can modify this on a per-content basis.
                            </p>
                            <Select onValueChange={handleDefaultAudienceChange} value={defaultAudience}>
                                <SelectTrigger className="input-select px-3 py-1 rounded-md text-sm">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="border border-eoe-border-main dark:border-eoe-dark-border-main">
                                    {privacyOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            <div className="flex items-center gap-2">
                                                <option.icon className="size-4" />
                                                <span>{option.label}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UserSettingsPrivacy 